import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable, of} from 'rxjs';

import {CountFavoritesService} from '@services/left-menu/favorites/count-favorites/count-favorites.service';
import {NotificationService} from '@services/notification/notification.service';
import {LeftMenuFavoriteService} from '@services/left-menu/favorites/left-menu-favorite.service';

import {MenuItem} from '@models/sidebar/menu-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {UserMenuItem} from '@models/sidebar/response/user-menu-item';
import {MenuFavoriteItem} from '@models/sidebar/response/menu-favorite-item';

@Injectable({
  providedIn: 'root'
})
export class ToggleFavoriteService {

  favoriteMenuItems: MenuFavoriteItem[] = [];
  private favoriteMenuItemsBehaviourSubject = new BehaviorSubject(this.favoriteMenuItems);
  updatedFavoriteMenuItems = this.favoriteMenuItemsBehaviourSubject.asObservable();

  private removeFavoriteItemSubject = new BehaviorSubject(new MenuItem());
  removeFavoriteItem = this.removeFavoriteItemSubject.asObservable();

  public static favoriteText = of('');
  public static unfavoredText = of('');
  private static maxNumberOfFavoritesReachedTitle = '';
  private static maxNumberOfFavoritesReachedMessage = '';
  private static errorFavoringItem: string = '';
  private static successFavoringItem: string = '';
  private static errorUnfavoringItem: string = '';
  private static successUnfavoringItem: string = '';
  private static CURRENT_PAGE_NAME = 'ToggleFavoriteService';

  constructor(
      private translate: TranslateService,
      private countFavoritesService: CountFavoritesService,
      private notificationService: NotificationService,
      private leftMenuFavoriteService: LeftMenuFavoriteService) {
    this.setTranslations();
  }

  public getTooltip(menuItem: MenuItem): Observable<string> {
    return menuItem.isFavorite ? ToggleFavoriteService.unfavoredText : ToggleFavoriteService.favoriteText;
  }

  public toggleFavorite(event: any, menuItem: MenuItem, firebaseUserId: string): void {
    event.stopPropagation();
    event.preventDefault();

    if (menuItem.isFavorite) {
      this.removeMenuItem(menuItem);
    } else {
      this.setAsFavorite(menuItem, firebaseUserId);
    }
  }

  public setFavoriteMenuItems(favoriteMenuItems: MenuFavoriteItem[]): void {
    this.favoriteMenuItems = [...favoriteMenuItems];
    this.countFavoritesService.setFavoritesNumber(this.favoriteMenuItems.length);
  }

  public isFavorite(menuItemId: string): boolean {
    let isFavorite = false;
    const foundMenuItem = this.favoriteMenuItems.filter(m => String(m.id) === String(menuItemId));
    if (foundMenuItem && foundMenuItem.length > 0) {
      isFavorite = true;
    }
    return isFavorite;
  }

  public updateFavoriteItemParent(itemId: string, parentId: string, parentName: string, isParentMachine: boolean) {
    for (const favoriteItem of this.favoriteMenuItems) {
      if (favoriteItem.id === itemId) {
        if (favoriteItem.itemParentId !== parentId) {
          console.log('parent of favorite item has changed to ' + parentId + ' name = ' + parentName);
          this.leftMenuFavoriteService.updateFavoriteItemParent(parentId, parentName, isParentMachine, favoriteItem.id).subscribe(() => {
          }, error => {
            console.log(error);
          });
        }
      }
    }
  }

  public broadcastUpdatedFavoriteMenuItems() {
    this.favoriteMenuItemsBehaviourSubject.next(this.favoriteMenuItems);
  }

  public broadcastRemoveFavoriteItem(removeFavoriteItem: MenuItem) {
    this.removeFavoriteItemSubject.next(removeFavoriteItem);
  }

  private setTranslations(): void {
    ToggleFavoriteService.favoriteText = this.translate.stream('Add to Favorites');
    ToggleFavoriteService.unfavoredText = this.translate.stream('Remove from Favorites');
    ToggleFavoriteService.errorFavoringItem = 'Error adding menu item to Favorites';
    ToggleFavoriteService.successFavoringItem = 'Successfully added %s of type %v to Favorites';
    ToggleFavoriteService.errorUnfavoringItem = 'Error removing from Favorites';
    ToggleFavoriteService.successUnfavoringItem = 'Successfully removed from Favorites';
    ToggleFavoriteService.maxNumberOfFavoritesReachedTitle = 'Limit reached';
    ToggleFavoriteService.maxNumberOfFavoritesReachedMessage = 'Menu item will not be added to Favorites.';
  }

  private setAsFavorite(menuItem: MenuItem, firebaseUserId: string): void {
    if (this.countFavoritesService.limitHasBeenReached) {
      this.notificationService.error(this.translate.instant(ToggleFavoriteService.maxNumberOfFavoritesReachedMessage), null, ToggleFavoriteService.CURRENT_PAGE_NAME, true, this.translate.instant(ToggleFavoriteService.maxNumberOfFavoritesReachedTitle));
    } else {
      const menuFavoriteItem = ToggleFavoriteService.getMenuFavoriteItem(menuItem, firebaseUserId);
      this.addToFavorite(menuFavoriteItem, menuItem);
    }
  }

  private addToFavorite(menuFavoriteItem: MenuFavoriteItem | null, menuItem: MenuItem): void {
    if (menuFavoriteItem) {
      this.leftMenuFavoriteService.addMenuItemFavorite(menuFavoriteItem).subscribe((result: MenuFavoriteItem) => {
        const message =  this.translate.instant(ToggleFavoriteService.successFavoringItem).replace('%v', result.type).replace('%s', result.menuItemName.toBold());
        this.notificationService.success(message, ToggleFavoriteService.CURRENT_PAGE_NAME, false);
        this.handleSuccessResponseForSettingItemAsFavorite(menuFavoriteItem, menuItem);
      }, error => {
        this.notificationService.error(this.translate.instant(ToggleFavoriteService.errorFavoringItem), error, ToggleFavoriteService.CURRENT_PAGE_NAME, true);
      });
    }
  }

  private removeMenuItem(menuItem: MenuItem): void {
    if (menuItem.id) {
      this.leftMenuFavoriteService.removeMenuFavoriteItem(menuItem.id).subscribe((_result: UserMenuItem[]) => {
        this.notificationService.success(this.translate.instant(ToggleFavoriteService.successUnfavoringItem), ToggleFavoriteService.CURRENT_PAGE_NAME, false);
        this.handleSuccessResponseForSettingItemAsUnfavorite(menuItem);
      }, error => {
        this.notificationService.error(this.translate.instant(ToggleFavoriteService.errorUnfavoringItem), error, ToggleFavoriteService.CURRENT_PAGE_NAME, true);
      });
    }
  }

  private static getIsParentMachine(menuItem: MenuItem): boolean {
    return menuItem.menuItemParent?.type === MenuItemType.MACHINE;
  }

  private static getMenuFavoriteItem(menuItem: MenuItem, firebaseUserId: string): MenuFavoriteItem | null {
    if (menuItem.id) {
      const menuFavoriteItem = new MenuFavoriteItem();
      menuFavoriteItem.firebaseParentId = firebaseUserId;
      menuFavoriteItem.menuItemName = menuItem.name;
      menuFavoriteItem.id = menuItem.id;
      if (menuItem.pageId) {
        menuFavoriteItem.pageId = menuItem.pageId;
      }
      if (menuItem.menuItemParent && menuItem.menuItemParent.id) {
        menuFavoriteItem.itemParentId = menuItem.menuItemParent?.id;
        menuFavoriteItem.itemParentName = menuItem.menuItemParent?.name;
      }
      // set nodeItemParentId if its a UserDefinedPage under a node
      if (menuItem.type === MenuItemType.USER_DEFINED_PAGE && menuItem.menuItemParent?.type === MenuItemType.NODE && menuItem.menuItemParent.menuItemParent?.id) {
        menuFavoriteItem.nodeItemParentId = menuItem.menuItemParent.menuItemParent?.id;
      }
      menuFavoriteItem.itemParentMachine = this.getIsParentMachine(menuItem);
      menuFavoriteItem.type = menuItem.type;
      menuFavoriteItem.url = menuItem.url;
      if (menuItem.machineCategory && !menuItem.machineCategory.isNullOrWhitespace()) {
        menuFavoriteItem.machineCategory = menuItem.machineCategory;
      }
      return menuFavoriteItem;
    }
    return null;
  }

  private handleSuccessResponseForSettingItemAsFavorite(menuFavoriteItem: MenuFavoriteItem, menuItem: MenuItem): void {
    menuItem.isFavorite = !menuItem.isFavorite;
    const filteredItems = this.favoriteMenuItems.filter(m => m.id === menuFavoriteItem.id);
    if (!filteredItems || filteredItems.length === 0) {
      this.favoriteMenuItems.push(menuFavoriteItem);
    }
    this.countFavoritesService.setFavoritesNumber(this.favoriteMenuItems.length);
    this.broadcastUpdatedFavoriteMenuItems();
  }

  private handleSuccessResponseForSettingItemAsUnfavorite(menuItem: MenuItem): void {
    menuItem.isFavorite = !menuItem.isFavorite;
    this.favoriteMenuItems = this.favoriteMenuItems.filter(m => String(m.id) !== String(menuItem.id));
    this.countFavoritesService.setFavoritesNumber(this.favoriteMenuItems.length);
    this.broadcastUpdatedFavoriteMenuItems();
    this.broadcastRemoveFavoriteItem(menuItem);
    this.broadcastRemoveFavoriteItem(new MenuItem());
  }

}
